/**
 * Reset Password Page Component
 *
 * Landing page for the emailed reset link. Reads the single-use token from the
 * `?token=` query param, collects a new password (with confirmation), and posts
 * it to the reset endpoint. On success, routes the admin back to the login page.
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApiService } from '../services/auth-api';
import { ADMIN_AUTH_ROUTES } from '../constants/routes';
import '../styles/AdminLogin.css';

const MIN_PASSWORD_LENGTH = 8;

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const message = await authApiService.resetPassword(token, password);
      setNotice(message);
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img
            src="/columbia_university_head.png"
            alt="Columbia University"
            className="login-logo"
          />
          <h1>CS Advising Chatbot</h1>
          <p className="login-subtitle">Choose a new password</p>
        </div>

        {!token ? (
          <div className="login-error">
            This reset link is missing its token. Please use the link from your email, or request a
            new one.
          </div>
        ) : done ? (
          <>
            {notice && <div className="login-notice">{notice}</div>}
            <button
              type="button"
              className="login-button"
              onClick={() => navigate(ADMIN_AUTH_ROUTES.LOGIN)}
            >
              Go to sign in
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="new-password">New password</label>
              <input
                id="new-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                required
                autoComplete="new-password"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirm-password">Confirm password</label>
              <input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter your new password"
                required
                autoComplete="new-password"
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" disabled={isLoading} className="login-button">
              {isLoading ? 'Resetting...' : 'Reset password'}
            </button>
            <button
              type="button"
              className="login-link-button"
              onClick={() => navigate(ADMIN_AUTH_ROUTES.LOGIN)}
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
